import { createCanvasNode, referenceTargetKey } from "./domain/canvas";
import type {
  CanvasGeometry,
  CanvasNode,
  EntityId,
  ReferenceDisplay,
  ReferenceTarget,
} from "./domain/types";

export type ReferenceResolution =
  | {
      status: "resolved";
      target: ReferenceTarget;
      title: string;
      summary?: string;
      icon?: string;
      version?: string | number;
    }
  | {
      status: "missing";
      target: ReferenceTarget;
      title: string;
      reason?: string;
      version?: string | number;
    };

export interface ReferenceResolveContext {
  signal: AbortSignal;
  cachedVersion?: string | number;
}

export interface ReferenceResolver {
  resolve(
    target: ReferenceTarget,
    context: ReferenceResolveContext,
  ): Promise<ReferenceResolution>;
  getContentVersion?(target: ReferenceTarget): string | number | undefined;
  subscribe?(listener: (target?: ReferenceTarget) => void): () => void;
}

/**
 * A resolver-owned URL, typically created with URL.createObjectURL().
 *
 * CanvasBoard calls release exactly once when the image changes, unmounts, or
 * resolves after its request was aborted. Stable URLs that need no cleanup can
 * still be returned as a plain string.
 */
export interface ResolvedCanvasAsset {
  url: string;
  release(): void;
}

export type CanvasAssetUrl = string | ResolvedCanvasAsset;

export interface CanvasAssetResolver {
  resolveUrl(
    attachmentId: EntityId,
    signal: AbortSignal,
  ): Promise<CanvasAssetUrl | null>;
}

export interface CreateReferenceNodeInput {
  id?: EntityId;
  target: ReferenceTarget;
  geometry?: Partial<CanvasGeometry>;
  display?: Partial<ReferenceDisplay>;
  parentFrameId?: EntityId;
  readingOrder?: number;
}

export function createReferenceNode(
  input: CreateReferenceNodeInput,
): CanvasNode {
  return createCanvasNode({
    ...(input.id === undefined ? {} : { id: input.id }),
    type: "reference",
    ...(input.geometry === undefined ? {} : { geometry: input.geometry }),
    props: {
      display: {
        showSummary: input.display?.showSummary ?? true,
        showIcon: input.display?.showIcon ?? true,
        ...(input.display?.accent === undefined
          ? {}
          : { accent: input.display.accent }),
        ...(input.display?.compact === undefined
          ? {}
          : { compact: input.display.compact }),
      },
    },
    reference: input.target,
    ...(input.parentFrameId === undefined
      ? {}
      : { parentFrameId: input.parentFrameId }),
    ...(input.readingOrder === undefined
      ? {}
      : { readingOrder: input.readingOrder }),
  });
}

export async function resolveCanvasReferences(
  nodes: Iterable<CanvasNode>,
  resolver: ReferenceResolver,
  signal: AbortSignal,
  options: ResolveCanvasReferencesOptions = {},
): Promise<Map<string, ReferenceResolution>> {
  return (options.cache ?? new ReferenceResolutionCache(options)).resolveAll(
    nodes,
    resolver,
    signal,
    options,
  );
}

export interface ResolveCanvasReferencesOptions {
  cache?: ReferenceResolutionCache;
  concurrency?: number;
  maxAgeMs?: number;
  force?: boolean;
}

interface CachedReference {
  resolution: ReferenceResolution;
  contentVersion?: string | number;
  storedAt: number;
}

function abortError(): Error {
  if (typeof DOMException === "function") {
    return new DOMException("Reference resolution aborted", "AbortError");
  }
  const error = new Error("Reference resolution aborted");
  error.name = "AbortError";
  return error;
}

export class ReferenceResolutionCache {
  readonly #entries = new Map<string, CachedReference>();
  readonly #pending = new Map<string, Promise<ReferenceResolution>>();
  readonly #defaultConcurrency: number;
  readonly #defaultMaxAgeMs: number;

  constructor(
    options: Pick<
      ResolveCanvasReferencesOptions,
      "concurrency" | "maxAgeMs"
    > = {},
  ) {
    this.#defaultConcurrency = Math.max(
      1,
      Math.floor(options.concurrency ?? 4),
    );
    this.#defaultMaxAgeMs = Math.max(0, options.maxAgeMs ?? 30_000);
  }

  invalidate(target?: ReferenceTarget): void {
    if (target === undefined) {
      this.#entries.clear();
      return;
    }
    this.#entries.delete(referenceTargetKey(target));
  }

  peek(target: ReferenceTarget): ReferenceResolution | undefined {
    return this.#entries.get(referenceTargetKey(target))?.resolution;
  }

  async resolveAll(
    nodes: Iterable<CanvasNode>,
    resolver: ReferenceResolver,
    signal: AbortSignal,
    options: ResolveCanvasReferencesOptions = {},
  ): Promise<Map<string, ReferenceResolution>> {
    if (signal.aborted) throw abortError();
    const targets = new Map<string, ReferenceTarget>();
    for (const node of nodes) {
      if (node.type === "reference" && node.reference !== undefined) {
        targets.set(referenceTargetKey(node.reference), node.reference);
      }
    }
    const queue = [...targets];
    const results = new Map<string, ReferenceResolution>();
    const concurrency = Math.max(
      1,
      Math.min(
        queue.length || 1,
        Math.floor(options.concurrency ?? this.#defaultConcurrency),
      ),
    );
    let cursor = 0;
    const workers = Array.from({ length: concurrency }, async () => {
      while (cursor < queue.length) {
        const entry = queue[cursor];
        cursor += 1;
        if (entry === undefined) continue;
        const [key, target] = entry;
        const resolution = await this.#resolveOne(
          key,
          target,
          resolver,
          signal,
          options,
        );
        results.set(key, resolution);
      }
    });
    await Promise.all(workers);
    return results;
  }

  async #resolveOne(
    key: string,
    target: ReferenceTarget,
    resolver: ReferenceResolver,
    signal: AbortSignal,
    options: ResolveCanvasReferencesOptions,
  ): Promise<ReferenceResolution> {
    if (signal.aborted) throw abortError();
    const cached = this.#entries.get(key);
    let sourceVersion: string | number | undefined;
    try {
      sourceVersion = resolver.getContentVersion?.(target);
    } catch (error) {
      return this.#cacheFailure(key, target, error);
    }
    const maxAgeMs = Math.max(0, options.maxAgeMs ?? this.#defaultMaxAgeMs);
    const cacheFresh =
      cached !== undefined &&
      Date.now() - cached.storedAt <= maxAgeMs &&
      (sourceVersion === undefined || sourceVersion === cached.contentVersion);
    if (!options.force && cacheFresh) return cached.resolution;

    const existing = this.#pending.get(key);
    if (existing !== undefined && !options.force) return existing;
    const pending = Promise.resolve()
      .then(() =>
        resolver.resolve(target, {
          signal,
          ...(cached?.contentVersion === undefined
            ? {}
            : { cachedVersion: cached.contentVersion }),
        }),
      )
      .then((resolution) => {
        if (signal.aborted) throw abortError();
        const contentVersion = resolution.version ?? sourceVersion;
        this.#entries.set(key, {
          resolution,
          ...(contentVersion === undefined ? {} : { contentVersion }),
          storedAt: Date.now(),
        });
        return resolution;
      })
      .catch((error: unknown): ReferenceResolution => {
        if (
          signal.aborted ||
          (error instanceof Error && error.name === "AbortError")
        ) {
          throw abortError();
        }
        return this.#cacheFailure(key, target, error, sourceVersion);
      })
      .finally(() => {
        if (this.#pending.get(key) === pending) this.#pending.delete(key);
      });
    this.#pending.set(key, pending);
    return pending;
  }

  #cacheFailure(
    key: string,
    target: ReferenceTarget,
    error: unknown,
    sourceVersion?: string | number,
  ): ReferenceResolution {
    const resolution: ReferenceResolution = {
      status: "missing",
      target,
      title: "",
      reason:
        error instanceof Error ? error.message : "Reference resolution failed",
      ...(sourceVersion === undefined ? {} : { version: sourceVersion }),
    };
    this.#entries.set(key, {
      resolution,
      ...(sourceVersion === undefined ? {} : { contentVersion: sourceVersion }),
      storedAt: Date.now(),
    });
    return resolution;
  }
}
